import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router';

import { DEMO_PATH_PREFIX, ENVIRONMENT, PATHS } from '@/shared/config';
import { hasDemoPathPrefix } from '@/shared/lib';

import { ReportLayout } from '@/features/reportStream';

import { AdditionalInformation } from '@/pages/AdditionalInformation/AdditionalInformation';
import { CBCTGPReportContainer } from '@/pages/CBCTGPReportContainer';
import { Community } from '@/pages/Community';
import { CompanySettings } from '@/pages/CompanySettings/CompanySettings';
import { ConfirmEmail } from '@/pages/ConfirmEmail/ConfirmEmail';
import { ConfirmSharing } from '@/pages/ConfirmSharing/ConfirmSharing';
import { DentalConsultation } from '@/pages/DentalConsultation';
import { DentalPhotos } from '@/pages/DentalPhotos/DentalPhotos';
import { Disclaimer } from '@/pages/Disclaimer/Disclaimer';
import { Encounters } from '@/pages/Encounters';
import { ErrorPage } from '@/pages/ErrorPage/ErrorPage';
import { FinishRegistration } from '@/pages/FinishRegistration';
import { GPReportLayout } from '@/pages/GpReport';
import { InviteSignUp } from '@/pages/InviteSignUp/InviteSignUp';
import { IOXRayReport } from '@/pages/IOXRayReport';
import { LandingRouter } from '@/pages/LandingRouter/LandingRouter';
import { NewPassword } from '@/pages/NewPassword/NewPassword';
import { OrganizationInvite } from '@/pages/OrganizationInvite/OrganizationInvite';
import { PanoReport } from '@/pages/PanoReport';
import { PanowingsReport } from '@/pages/PanowingsReport';
import { PatientDocumentPage } from '@/pages/PatientDocument';
import {
  PatientLayout,
  PatientProfilePageContainer,
} from '@/pages/PatientProfile';
import { PatientRecording } from '@/pages/PatientRecording';
import { PatientRecordings } from '@/pages/PatientRecordings';
import { PatientRecordingStart } from '@/pages/PatientRecordingStart';
import { Patients } from '@/pages/Patients/Patients';
import { PersonalSettings } from '@/pages/PersonalSettings/PersonalSettings';
import { PreviewReport } from '@/pages/PreviewReport/PreviewReport';
import { ResetPassword } from '@/pages/ResetPassword/ResetPassword';
import { SegmentationReport } from '@/pages/SegmentationReport/SegmentationReport';
import { SignIn } from '@/pages/SignIn/SignIn';
import { SignUp } from '@/pages/SignUp/SignUp';
import { Tooth } from '@/pages/Tooth/Tooth';
import { TranscriptorLanding } from '@/pages/TranscriptorLanding';
import { TranscriptorTemplates } from '@/pages/TranscriptorTemplates';

import { ChatRoutes } from '../providers/ChatRoutes';
import { DemoRouteSync } from '../providers/DemoRouteSync';
import { ProtectedRoutes } from '../providers/ProtectedRoutes';
import { Report2DLayout } from '../providers/Report2DLayout';

import { Playground } from '@/playground';
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<DemoRouteSync />}>
      <Route path={PATHS.newPassword} element={<NewPassword />} />

      <Route element={<ChatRoutes />}>
        <Route path={PATHS.signUp} element={<SignUp />} />
        <Route path={PATHS.signIn} element={<SignIn />} />
        <Route path={PATHS.confirmEmail} element={<ConfirmEmail />} />
        <Route path={PATHS.resetPassword} element={<ResetPassword />} />
        <Route
          path={PATHS.finishRegistration}
          element={<FinishRegistration />}
        />
        <Route path={PATHS.invite} element={<InviteSignUp />} />

        <Route element={<ProtectedRoutes />}>
          <Route path={PATHS.main} element={<Patients />} />
          <Route path={PATHS.patients} element={<Patients />} />
          <Route path={PATHS.personalSettings} element={<PersonalSettings />} />
          <Route path={PATHS.companySettings} element={<CompanySettings />} />
          <Route path={PATHS.encounter} element={<Encounters />} />
          <Route path={PATHS.community} element={<Community />} />

          <Route path={PATHS.patient} element={<PatientLayout />}>
            <Route index element={<PatientProfilePageContainer />} />
            <Route
              path={PATHS.patientDocument}
              element={<PatientDocumentPage />}
            />
            <Route
              path={PATHS.report}
              element={<ReportLayout key="main-report-layout" />}
            >
              <Route path={PATHS.gp} element={<GPReportLayout />}>
                <Route index element={<CBCTGPReportContainer />} />
                <Route path={PATHS.gpReportAllSlices} element={<Tooth />} />
              </Route>
              <Route element={<Report2DLayout />}>
                <Route path={PATHS.ioxRay} element={<IOXRayReport />} />
                <Route path={PATHS.pano} element={<PanoReport />} />
                <Route path={PATHS.bitewings} element={<PanowingsReport />} />
              </Route>
              <Route path={PATHS.preview} element={<PreviewReport />} />
            </Route>
            <Route path={PATHS.dentalPhotos} element={<DentalPhotos />} />
            <Route
              path={PATHS.patientRecordings}
              element={<PatientRecordings />}
            />
            <Route
              path={PATHS.patientRecordingStart}
              element={<PatientRecordingStart />}
            />
            <Route
              path={PATHS.patientRecording}
              element={<PatientRecording />}
            />
          </Route>

          <Route element={<Report2DLayout />}>
            <Route path={PATHS.consultation} element={<DentalConsultation />} />
          </Route>

          <Route
            path={PATHS.segmentationReport}
            element={<SegmentationReport />}
          />
          <Route path={PATHS.disclaimer} element={<Disclaimer />} />
          <Route path={PATHS.confirmSharing} element={<ConfirmSharing />} />
          <Route
            path={PATHS.additionalInfo}
            element={<AdditionalInformation />}
          />
          <Route
            path={PATHS.organization_invite}
            element={<OrganizationInvite />}
          />
          <Route path={PATHS.landingRouter} element={<LandingRouter />} />
          <Route
            path={PATHS.transcriptorLanding}
            element={<TranscriptorLanding />}
          />
          <Route
            path={PATHS.transcriptorTemplates}
            element={<TranscriptorTemplates />}
          />

          <Route path={PATHS.previewReport} element={<PreviewReport />} />
          {/*
           NOTE: Deliberately a sibling of PatientLayout, not nested under it — this
           page only needs report+patient identity (now delivered by reportDataStream
           itself, see useReportDataStream's HistoricalPatient case) and must not drag
           in PatientLayout's usePatientProfileStream (study/sharing/albums/org staff),
           whose unrelated failures used to blank this page's print output.
         */}
          <Route
            path={PATHS.patient + '/' + PATHS.preview2D}
            element={<ReportLayout key="preview2d" />}
          >
            <Route index element={<PreviewReport />} />
          </Route>
          {/*
           NOTE: This is playground for development isolated component with possibility to use all project components and communicate with the backend. This is not allowed to be on production.
           For some reason react router is not working if put all this code in separate component to not see this mess here.
         */}
          {ENVIRONMENT !== 'production' ? (
            <Route path="/playground" element={<Playground />} />
          ) : null}
        </Route>

        <Route path={PATHS.error} element={<ErrorPage />} />
      </Route>
    </Route>,
  ),
  {
    basename: hasDemoPathPrefix(window.location.pathname)
      ? DEMO_PATH_PREFIX
      : undefined,
  },
);
